import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Chip,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent,
  Backdrop, CircularProgress, Snackbar, Alert,
  Paper, InputAdornment, TextField,
} from '@mui/material';
import {
  Add, Close, Shield, OpenInNew, CloudUpload, CompareArrows, Search, AutoAwesome,
} from '@mui/icons-material';
import { connect } from 'react-redux';
import ProductForm from './ProductForm';
import TemplateGalleryDialog from './TemplateGalleryDialog';
import { hashHistory } from 'react-router';
import { DraftUploadDialogContainer } from '../../../../containers/GenericProductConfig/DraftUploadDialogContainer';
import { DraftsTableContainer } from '../../../../containers/GenericProductConfig/DraftsTableContainer';
import { ReviewDraftProductContainer } from '../../../../containers/GenericProductConfig/ReviewDraftProductContainer';
import CompareQuotationDialog from '../../../../containers/GenericProductConfig/components/QuotationDialog/CompareQuotationDialog';
import {
  compareQuotationsRequest,
  resetQuotationResults,
  fetchPartnerDraftsRequest,
  resetDraftState,
} from '../../../../containers/GenericProductConfig/actions.es6.js';
import { canImportProductDraft, canAccessQuotations, canCreateProduct } from '../../../../lib/access.es6.js';
import useCurrencyOptions from '../../../../hooks/useCurrencyOptions';

var TH_STYLE = {
  fontSize: 11, fontWeight: 700, color: '#6B7280',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
  py: 1.25, px: 2,
};

var TD_STYLE = {
  fontSize: 13, color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  py: 1.5, px: 2,
};

var COVER_COLORS = {
  individual: { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },
  group: { bg: '#F0FDF4', text: '#15803D', border: '#BBF7D0' },
  family: { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' },
};

function coverChipStyle(coverType) {
  var key = (coverType || '').toLowerCase();
  var c = COVER_COLORS[key] || { bg: '#F5F3FF', text: '#6D28D9', border: '#DDD6FE' };
  return {
    height: 20, fontSize: 10, fontWeight: 600,
    bgcolor: c.bg, color: c.text, border: '1px solid ' + c.border,
  };
}

// Prototype-style summary card (Total / Active / Inactive). accent tints the value.
function StatCard(props) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1, minWidth: 140, px: 2.5, py: 1.75,
        border: '1px solid #E5E7EB', borderRadius: 2, bgcolor: '#fff',
      }}
    >
      <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1, color: props.accent || '#173E4B' }}>
        {props.value}
      </Typography>
      <Typography sx={{ fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B7280', mt: 0.25 }}>
        {props.label}
      </Typography>
    </Paper>
  );
}

// Sub-entities and server-owned fields are not part of the create-product form;
// strip them so a draft prefills only the top-level product config the maker
// reviews. Packages/benefits/add-ons are added afterwards via the product view.
var DRAFT_NON_FORM_KEYS = [
  'packages',
  'add_ons',
  'cashback',
  'age_validation_rules',
  'purchase_constraints',
  'membership_config',
  'membership_configs',
  'premiums',
  'product_premiums',
  'groups',
  'group',
  'renewal',
  'guid',
  'version',
  'partner_guid',
  'source_template_guid',
  'created_at',
  'updated_at',
  '_id',
  '__v',
];

var draftToCreateFormData = function(draftProduct) {
  var out = {};
  Object.keys(draftProduct || {}).forEach(function(k) {
    if (DRAFT_NON_FORM_KEYS.indexOf(k) === -1) out[k] = draftProduct[k];
  });
  return out;
};

const Products = function(props) {
  var genericProducts = props.genericProductsData || {};

  var [search, setSearch] = useState('');
  var [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  var [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
  var [isTemplateGalleryOpen, setIsTemplateGalleryOpen] = useState(false);
  var [compareDialogOpen, setCompareDialogOpen] = useState(false);
  // When set, the Create Product form is pre-filled from an AI draft for review.
  var [draftInitialData, setDraftInitialData] = useState(null);
  var [snackbarOpen, setSnackbarOpen] = useState(false);
  var [snackbarMessage, setSnackbarMessage] = useState('');
  var [snackbarSeverity, setSnackbarSeverity] = useState('success');

  var currencyOptions = useCurrencyOptions(props.countryState, props.dispatch);

  useEffect(function() {
    if (props.productSuccess) {
      setSnackbarMessage('Product created successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsCreateModalOpen(false);
      setDraftInitialData(null);
      setTimeout(function() {
        props.resetProductSuccess && props.resetProductSuccess();
      }, 100);
    }
  }, [props.productSuccess]);

  useEffect(function() {
    if (props.productError) {
      setSnackbarMessage('Error: ' + (props.productError.message || props.productError || 'Something went wrong'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setTimeout(function() {
        props.resetProductError && props.resetProductError();
      }, 100);
    }
  }, [props.productError]);

  // Once the drafting job is enqueued (job id assigned), close the upload modal
  // and surface the drafts table — the job then shows there as "Pending" and the
  // table auto-polls its status, rather than making the user wait in the modal.
  useEffect(function() {
    if (isDraftModalOpen && genericProducts.draftJobId) {
      setIsDraftModalOpen(false);
      props.dispatch(fetchPartnerDraftsRequest());
      props.dispatch(resetDraftState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genericProducts.draftJobId]);

  var handleCreateProduct = function(productData) {
    if (props.onProductInfoSubmit) {
      props.onProductInfoSubmit(productData);
    }
  };

  var handleOpenCompareDialog = function() {
    setCompareDialogOpen(true);
  };

  var handleCloseCompareDialog = function() {
    setCompareDialogOpen(false);
    props.dispatch(resetQuotationResults());
  };

  var handleCompare = function(payload) {
    props.dispatch(compareQuotationsRequest(payload));
  };

  // Draft accepted → prefill the create form with the draft's product-level
  // config and open it for human review (keeps creation inside the normal
  // create flow + maker-checker gate).
  var handleUseDraft = function(draftProduct) {
    setDraftInitialData(draftToCreateFormData(draftProduct));
    setIsDraftModalOpen(false);
    setIsCreateModalOpen(true);
  };

  var products = props.products || [];

  // Stats computed from the full set; the table shows the filtered subset.
  var activeCount = products.filter(function(p) { return p.active !== false; }).length;
  var inactiveCount = products.length - activeCount;

  var q = search.trim().toLowerCase();
  var filtered = !q ? products : products.filter(function(p) {
    return (p.name || '').toLowerCase().indexOf(q) !== -1
      || (p.code || '').toLowerCase().indexOf(q) !== -1;
  });

  return (
    <Box>
      {/* Summary stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <StatCard value={products.length} label="Total Products" accent="#173E4B" />
        <StatCard value={activeCount} label="Active" accent="#15803D" />
        <StatCard value={inactiveCount} label="Inactive" accent="#B91C1C" />
      </Box>

      {/* Filter + action buttons */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 260 }}>
          <TextField
            size="small"
            placeholder="Filter by name or code…"
            value={search}
            onChange={function(e) { setSearch(e.target.value); }}
            sx={{ width: 280, maxWidth: '100%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: '#9CA3AF' }} />
                </InputAdornment>
              ),
            }}
          />
          <Typography sx={{ fontSize: 12.5, color: '#6B7280', whiteSpace: 'nowrap' }}>
            {filtered.length} of {products.length} products
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {canCreateProduct() && (
          <Button
            variant="outlined"
            startIcon={<AutoAwesome />}
            onClick={function() { setIsTemplateGalleryOpen(true); }}
            size="small"
            sx={{
              textTransform: 'none',
              color: '#173E4B',
              borderColor: '#fba92c',
              '&:hover': { borderColor: '#f97316', bgcolor: '#fff7ed' },
            }}
          >
            Start from Template
          </Button>
        )}
        {canImportProductDraft() && (
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={function() { setIsDraftModalOpen(true); }}
            size="small"
            sx={{
              textTransform: 'none',
              color: '#173E4B',
              borderColor: '#fba92c',
              '&:hover': { borderColor: '#f97316', bgcolor: '#fff7ed' },
            }}
          >
            Generate from Document
          </Button>
        )}
        {canAccessQuotations() && (
          <Button
            variant="outlined"
            startIcon={<CompareArrows />}
            onClick={handleOpenCompareDialog}
            size="small"
            sx={{
              textTransform: 'none',
              color: '#173E4B',
              borderColor: '#173E4B',
              '&:hover': { borderColor: '#173E4B', bgcolor: 'rgba(23,62,75,0.04)' },
            }}
          >
            Compare Quotations
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={function() { setIsCreateModalOpen(true); }}
          size="small"
          sx={{
            textTransform: 'none',
            bgcolor: '#fba92c',
            fontWeight: 600,
            '&:hover': { bgcolor: '#f97316' },
          }}
        >
          Create Product
        </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{
        border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden',
      }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={TH_STYLE}>Product</TableCell>
              <TableCell sx={TH_STYLE}>Code</TableCell>
              <TableCell sx={TH_STYLE}>Type</TableCell>
              <TableCell sx={TH_STYLE}>Cover</TableCell>
              <TableCell sx={TH_STYLE}>Lapse Period</TableCell>
              <TableCell sx={TH_STYLE}>Status</TableCell>
              <TableCell sx={Object.assign({}, TH_STYLE, { textAlign: 'right' })}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Shield sx={{ fontSize: 40, color: '#D1D5DB', mb: 1 }} />
                    <Typography sx={{ fontSize: 14, color: '#9CA3AF' }}>
                      {q ? 'No products match your filter.' : 'No product templates yet'}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#D1D5DB', mt: 0.5 }}>
                      {q
                        ? 'Try a different name or code.'
                        : 'Click "Create Product" to add the first template'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filtered.map(function(product) {
              var lapseText = product.lapse_period_days
                ? product.lapse_period_days + ' days'
                : null;
              var label = (product.name || product.code || '').replace(/_/g, ' ');
              return (
                <TableRow
                  key={product.guid || product.code}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#FAFAFA' },
                    '&:last-child td': { border: 0 },
                  }}
                  onClick={function() {
                    hashHistory.push('admin/view-generic-product/' + product.guid);
                  }}
                >
                  <TableCell sx={TD_STYLE}>
                    <Chip
                      label={label}
                      size="small"
                      sx={{
                        height: 22, fontSize: 11, fontWeight: 600,
                        bgcolor: '#F0FDF4', color: '#15803D',
                        border: '1px solid #BBF7D0', maxWidth: 240,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={TD_STYLE}>
                    <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: '#6B7280' }}>
                      {product.code || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={TD_STYLE}>
                    {product.type ? (
                      <Chip label={product.type} size="small" sx={{
                        height: 20, fontSize: 10, fontWeight: 500,
                        bgcolor: '#F5F3FF', color: '#6D28D9',
                        border: '1px solid #DDD6FE',
                      }} />
                    ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                  </TableCell>
                  <TableCell sx={TD_STYLE}>
                    {product.cover_type ? (
                      <Chip label={product.cover_type} size="small"
                        sx={coverChipStyle(product.cover_type)}
                      />
                    ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                  </TableCell>
                  <TableCell sx={TD_STYLE}>
                    <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                      {lapseText || <span style={{ color: '#D1D5DB' }}>—</span>}
                    </Typography>
                  </TableCell>
                  <TableCell sx={TD_STYLE}>
                    <Chip
                      label={product.active !== false ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        height: 20, fontSize: 10, fontWeight: 600,
                        bgcolor: product.active !== false ? '#F0FDF4' : '#FEF2F2',
                        color: product.active !== false ? '#15803D' : '#B91C1C',
                        border: product.active !== false
                          ? '1px solid #BBF7D0'
                          : '1px solid #FECACA',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={Object.assign({}, TD_STYLE, { textAlign: 'right' })}>
                    <Tooltip title="View Product">
                      <IconButton
                        size="small"
                        onClick={function(e) {
                          e.stopPropagation();
                          hashHistory.push('admin/view-generic-product/' + product.guid);
                        }}
                        sx={{ color: '#0D47A1', p: 0.75 }}
                      >
                        <OpenInNew sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Product Modal */}
      <Dialog
        open={isCreateModalOpen && !props.isLoading}
        onClose={function() { setIsCreateModalOpen(false); setDraftInitialData(null); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{
          fontWeight: 700, fontSize: 16, color: '#111827',
          pb: 1, display: 'flex', justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          {draftInitialData ? 'Review AI-generated draft' : 'Create Product Template'}
          <IconButton
            onClick={function() { setIsCreateModalOpen(false); setDraftInitialData(null); }}
            size="small" sx={{ color: '#6B7280' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1 }}>
          {draftInitialData && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review and edit the pre-filled fields before creating. Packages,
              add-ons and other sub-entities are added after the product is
              created.
            </Typography>
          )}
          <ProductForm
            key={draftInitialData ? 'from-draft' : 'blank'}
            initialData={draftInitialData}
            onSubmit={handleCreateProduct}
            currencyOptions={currencyOptions}
          />
        </DialogContent>
      </Dialog>

      {/* Start-from-Template gallery (clone an archetype → live product) */}
      <TemplateGalleryDialog
        open={isTemplateGalleryOpen}
        onClose={function() { setIsTemplateGalleryOpen(false); }}
      />

      {canImportProductDraft() && (
        <>
          {/* Generate-from-Document Dialog (AI draft → review → create) */}
          <DraftUploadDialogContainer
            open={isDraftModalOpen}
            onClose={function() { setIsDraftModalOpen(false); }}
            onUseDraft={handleUseDraft}
          />

          {/* All drafts ever generated for this partner (status + review) */}
          <DraftsTableContainer />

          {/* Full read-only review of a drafted product → approve & import */}
          <ReviewDraftProductContainer />
        </>
      )}

      {canAccessQuotations() && (
        <CompareQuotationDialog
          open={compareDialogOpen}
          onClose={handleCloseCompareDialog}
          quotationResults={genericProducts && genericProducts.quotationResults}
          quotationLoader={genericProducts && genericProducts.quotationLoader}
          quotationError={genericProducts && genericProducts.quotationError}
          onCompare={handleCompare}
        />
      )}

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: function(theme) { return theme.zIndex.drawer + 1; } }}
        open={props.isLoading || false}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Creating product...
          </Typography>
        </Box>
      </Backdrop>

      {/* Success / Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={function() { setSnackbarOpen(false); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={function() { setSnackbarOpen(false); }}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default connect(function(state) {
  return { countryState: state.country };
})(Products);
